/**
 * Zustand store for the Visual Workflow Builder.
 * Manages React Flow graph state (nodes, edges) and builder UI state.
 */
import { create } from 'zustand';
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
} from '@xyflow/react';

/**
 * Generate a unique step ID for new nodes.
 * Format: step_[8-char hex] to match backend WorkflowNode.id pattern.
 */
const generateStepId = () => {
  const hex = Math.random().toString(16).substring(2, 10);
  return `step_${hex}`;
};

/**
 * Stable client-only id for a template variable row (WR-04).
 * Used as the React key in VariablesEditor so removing/reordering a row does not
 * rebind stale input state. Stripped from the payload before persisting.
 */
const genVarUid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Default config values for each service type.
 */
const SERVICE_DEFAULTS = {
  doqsolv: { label: 'Generate Document', color: '#6366F1' },
  live_stamping: { label: 'Live Stamping', color: '#10B981' },
  esign: { label: 'eSign', color: '#3B82F6' },
  mock: { label: 'Mock Service', color: '#6B7280' },
};

const useBuilderStore = create((set, get) => ({
  // --- Graph State ---
  nodes: [],
  edges: [],

  // --- Template Variables State ---
  // Template-scoped variable definitions, each shaped {name, type, description, required}.
  // type ∈ {string, bool, file} — consumed by WorkflowStarter's dynamic form.
  variables: [],

  // --- UI State ---
  selectedNodeId: null,
  templateId: null,       // currently loaded template ID (null = new)
  templateName: '',
  templateDescription: '',
  isDirty: false,         // has unsaved changes

  // --- Node/Edge Handlers (called by React Flow) ---
  onNodesChange: (changes) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
      isDirty: true,
    });
  },

  onEdgesChange: (changes) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
      isDirty: true,
    });
  },

  onConnect: (connection) => {
    set({
      edges: addEdge(
        {
          ...connection,
          animated: true,
          style: { stroke: '#6366F1', strokeWidth: 2 },
          markerEnd: { type: 'arrowclosed', color: '#6366F1' },
        },
        get().edges
      ),
      isDirty: true,
    });
  },

  // --- Node Actions ---
  addNode: (serviceType, position) => {
    const defaults = SERVICE_DEFAULTS[serviceType] || SERVICE_DEFAULTS.mock;
    const id = generateStepId();
    const newNode = {
      id,
      type: serviceType,
      position,
      data: {
        label: defaults.label,
        serviceType,
        name: defaults.label,
        config: {},
        inputs: {},
      },
    };
    set({
      nodes: [...get().nodes, newNode],
      isDirty: true,
    });
    return id;
  },

  updateNodeData: (nodeId, dataUpdates) => {
    set({
      nodes: get().nodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...dataUpdates } }
          : node
      ),
      isDirty: true,
    });
  },

  deleteNode: (nodeId) => {
    set({
      nodes: get().nodes.filter((n) => n.id !== nodeId),
      edges: get().edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      selectedNodeId: get().selectedNodeId === nodeId ? null : get().selectedNodeId,
      isDirty: true,
    });
  },

  selectNode: (nodeId) => {
    set({ selectedNodeId: nodeId });
  },

  clearSelection: () => {
    set({ selectedNodeId: null });
  },

  // --- Template Actions ---
  setNodes: (nodes) => set({ nodes }),
  setEdges: (edges) => set({ edges }),

  setTemplate: (id, name, description) => {
    set({ templateId: id, templateName: name, templateDescription: description || '' });
  },

  setTemplateName: (name) => set({ templateName: name, isDirty: true }),
  setTemplateDescription: (desc) => set({ templateDescription: desc, isDirty: true }),

  // --- Template Variable Actions ---
  // Replace the whole variables array (used when loading a template on edit/clone).
  // Backfill a stable _uid on each row (WR-04) since variables loaded from the backend
  // have none; the _uid is client-only and stripped again before the next save.
  setVariables: (variables) => set({
    variables: (variables || []).map((v) => (v && v._uid ? v : { ...(v || {}), _uid: genVarUid() })),
    isDirty: true,
  }),

  // Append one new variable definition.
  addVariable: (variable) => set({
    variables: [
      ...get().variables,
      { name: '', type: 'string', description: '', required: true, ...(variable || {}), _uid: genVarUid() },
    ],
    isDirty: true,
  }),

  // Merge a partial patch into the variable at index i.
  updateVariable: (index, patch) => set({
    variables: get().variables.map((v, i) => (i === index ? { ...v, ...patch } : v)),
    isDirty: true,
  }),

  // Remove the variable at index i.
  removeVariable: (index) => set({
    variables: get().variables.filter((_, i) => i !== index),
    isDirty: true,
  }),

  clearCanvas: () => {
    set({
      nodes: [],
      edges: [],
      variables: [],
      selectedNodeId: null,
      templateId: null,
      templateName: '',
      templateDescription: '',
      isDirty: false,
    });
  },

  markClean: () => set({ isDirty: false }),
}));

export default useBuilderStore;
export { SERVICE_DEFAULTS };
